import type { PaneTree, SplitOrientation } from "./tree";

export type Rect = { top: number; left: number; width: number; height: number };

export type PaneBox = { id: string; content: string | undefined; rect: Rect };

export type DividerInfo = { splitId: string; orientation: SplitOrientation; splitRect: Rect; fraction: number };

export type Layout = { panes: PaneBox[]; dividers: DividerInfo[] };

const FULL: Rect = { top: 0, left: 0, width: 100, height: 100 };

/**
 * Flattens the pane tree into absolutely-positioned boxes (percent of the container)
 * keyed by pane id, plus one divider per split. Restructuring the tree only changes
 * each box's rect, so a surviving pane keeps its React identity (no remount).
 */
export function computeLayout(tree: PaneTree, sizes: Record<string, number>, rect: Rect = FULL): Layout {
  if (tree.kind === "leaf") {
    return { panes: [{ id: tree.id, content: tree.content, rect }], dividers: [] };
  }

  const fraction = sizes[tree.id] ?? 0.5;
  let firstRect: Rect;
  let secondRect: Rect;
  if (tree.orientation === "horizontal") {
    firstRect = { ...rect, width: rect.width * fraction };
    secondRect = { ...rect, left: rect.left + rect.width * fraction, width: rect.width * (1 - fraction) };
  } else {
    firstRect = { ...rect, height: rect.height * fraction };
    secondRect = { ...rect, top: rect.top + rect.height * fraction, height: rect.height * (1 - fraction) };
  }

  const first = computeLayout(tree.first, sizes, firstRect);
  const second = computeLayout(tree.second, sizes, secondRect);
  return {
    panes: [...first.panes, ...second.panes],
    dividers: [
      { splitId: tree.id, orientation: tree.orientation, splitRect: rect, fraction },
      ...first.dividers,
      ...second.dividers,
    ],
  };
}
